import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import api, { getToken, setToken, getStoredUser, setStoredUser } from "@/lib/api";

const Ctx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => getStoredUser());
  const [loading, setLoading] = useState(!!getToken() && !getStoredUser());
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "light");

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("theme", theme);
  }, [theme]);

  // Hydrate user from /api/me if we have a token but no cached user
  useEffect(() => {
    let cancelled = false;
    const token = getToken();
    if (!token || user) return () => { cancelled = true; };

    setLoading(true);
    api.me()
      .then((res) => {
        if (cancelled) return;
        const u = res?.user || res;
        if (u) { setUser(u); setStoredUser(u); }
      })
      .catch((e) => {
        if (cancelled) return;
        console.warn("Session hydration failed, clearing token:", e?.message || e);
        setToken(null); setStoredUser(null); setUser(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
    // Runs once on mount only — we deliberately don't want this to re-run
    // when `user` changes (which would loop).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const finalise = (token, u) => {
    setToken(token);
    setStoredUser(u);
    setUser(u);
    return u;
  };

  const value = useMemo(() => ({
    user,
    loading,
    isAuthed: !!user && !!getToken(),
    theme,
    setTheme,
    toggleTheme: () => setTheme((t) => (t === "dark" ? "light" : "dark")),

    async loginPassword(email, password) {
      const res = await api.login(email, password);
      return finalise(res.token, res.user);
    },
    async requestOtp(email) {
      return api.otpRequest(email);
    },
    async register(payload) {
      return api.register(payload);
    },
    async verifyOtp(email, code) {
      const res = await api.otpVerify(email, code);
      return finalise(res.token, res.user);
    },
    async logout() {
      try { await api.logout(); } catch (e) { console.warn("logout API failed (clearing local session anyway):", e?.message || e); }
      setToken(null); setStoredUser(null); setUser(null);
    },
    async updatePhone(phone) {
      const res = await api.updateMe({ phone });
      const u = { ...user, phone: res.phone ?? phone };
      setStoredUser(u); setUser(u);
      return u;
    },
    // Used by the one-time student profile step (name, USN, department,
    // semester, section, phone, email) gating the first ticket raised.
    async updateProfile(fields) {
      const res = await api.updateMe(fields);
      const u = res.user || { ...user, ...fields };
      setStoredUser(u); setUser(u);
      return u;
    },
  }), [user, loading, theme]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useAuth = () => {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth outside AuthProvider");
  return v;
};

// Role helpers
export const isSuperadmin = (u) => u?.role === "superadmin";
export const isAdmin = (u) => u?.role === "admin" || isSuperadmin(u);
export const isTechnician = (u) => u?.role === "technician";
// Works a queue rather than the whole institute: technicians and category heads.
// ("hod"/"dean_welfare"/"coe" were an earlier model — they are now one role: "authority".)
export const isQueueOwner = (u) => ["technician", "authority"].includes(u?.role);
export const isCategoryHead = (u) => u?.role === "authority";
export const homeFor = (u) => {
  if (isSuperadmin(u)) return "/superadmin";
  if (u?.role === "admin" || isQueueOwner(u)) return "/admin";
  return "/portal";
};
